import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  FlaskConical, BeakerIcon, Utensils, Wheat, Cloud, Sparkles,
  Target, Compass, Award,
} from "lucide-react";
import { usePageContent } from "@/context/PageContentContext";

const PILLAR_ICONS = [Compass, Target, Award];
const SERVICE_ICONS = [FlaskConical, BeakerIcon, Utensils, Wheat, Cloud, Sparkles];

export default function About() {
  const { content } = usePageContent("about");

  return (
    <div data-testid="page-about" className="bg-cream">
      {/* Hero */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-28 relative overflow-hidden">
        <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-[var(--k-olive-soft)] opacity-40 blur-[100px]" />
        <div className="container-k px-6 md:px-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <div className="font-mono-label text-[var(--k-green)] mb-6">{content.hero_eyebrow}</div>
            <h1
              data-testid="about-headline"
              className="font-serif-display text-4xl sm:text-5xl md:text-7xl text-[var(--k-text)] leading-[1.02]"
            >
              {content.hero_title_pre}{" "}
              <em className="green-gradient-text not-italic">{content.hero_title_em}</em> {content.hero_title_post}
            </h1>
            <p className="mt-8 text-lg text-[var(--k-text-2)] leading-relaxed max-w-2xl">
              {content.hero_intro}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Vision / Mission / Why */}
      <section className="section bg-beige" data-testid="about-pillars">
        <div className="container-k grid md:grid-cols-3 gap-7">
          {(content.pillars || []).map((p, i) => {
            const Icon = PILLAR_ICONS[i % PILLAR_ICONS.length];
            return (
              <motion.div
                key={p.title || i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.7 }}
                className="organic-card p-10"
                data-testid={`about-pillar-${i}`}
              >
                <Icon className="w-7 h-7 text-[var(--k-green)] mb-6" strokeWidth={1.3} />
                <h3 className="font-serif-display text-3xl text-[var(--k-text)] mb-3">{p.title}</h3>
                <p className="text-[var(--k-text-2)] leading-relaxed">{p.body}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Services */}
      <section className="section bg-cream" data-testid="about-services">
        <div className="container-k">
          <div className="max-w-3xl mb-14">
            <div className="font-mono-label text-[var(--k-gold)] mb-4">{content.services_eyebrow}</div>
            <h2 className="font-serif-display text-3xl sm:text-4xl md:text-5xl text-[var(--k-text)] leading-tight">
              {content.services_title_pre}{" "}
              <em className="green-gradient-text not-italic">{content.services_title_em}</em>{content.services_title_post}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {(content.services || []).map((c, i) => {
              const Icon = SERVICE_ICONS[i % SERVICE_ICONS.length];
              return (
                <motion.div
                  key={c.title || i}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.6, delay: i * 0.07 }}
                  data-testid={`about-service-${i}`}
                  className="organic-card p-9"
                >
                  <div className="w-12 h-12 rounded-full bg-[var(--k-bg-2)] flex items-center justify-center border border-[var(--k-line)] mb-6">
                    <Icon className="w-5 h-5 text-[var(--k-green)]" strokeWidth={1.4} />
                  </div>
                  <h3 className="font-serif-display text-2xl text-[var(--k-text)] mb-3">{c.title}</h3>
                  <p className="text-[var(--k-text-2)] text-sm leading-relaxed">{c.desc}</p>
                </motion.div>
              );
            })}
          </div>

          <div className="mt-16 text-center">
            <Link to="/contact" data-testid="about-cta" className="btn-green">
              {content.cta_label}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
